import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import ListPagination from '../components/common/ListPagination';
import AwardeeFormModal from '../components/tenders/AwardeeFormModal';
import AwardeeTenderCountPill from '../components/tenders/AwardeeTenderCountPill';
import {
  tenderAwardeeService,
  TenderAwardee,
  TenderAwardeeFilters,
} from '../services/tenderAwardeeService';
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate } from '../utils/dateUtils';
import { useListUrlState } from '../hooks/useListUrlState';
import { ListFilterSpec } from '../utils/listQueryParams';

const AWARDEE_LIST_FILTER_SPEC: ListFilterSpec<TenderAwardeeFilters> = {
  strings: ['search'],
};

const TenderAwardees: React.FC = () => {
  const navigate = useNavigate();
  const [awardees, setAwardees] = useState<TenderAwardee[]>([]);
  const [total, setTotal] = useState(0);
  const { page, setPage, perPage, setPerPage, filters, setFilters } = useListUrlState<TenderAwardeeFilters>({
    spec: AWARDEE_LIST_FILTER_SPEC,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(() => (typeof filters.search === 'string' ? filters.search : ''));
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TenderAwardee | null>(null);
  const hasLoadedRef = useRef(false);

  const load = async () => {
    try {
      setLoading(true);
      const result = await tenderAwardeeService.getAll({
        ...filters,
        page,
        per_page: perPage,
      });
      setAwardees(result.data);
      setTotal(result.total);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tender awardees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    load();
  }, []);

  useEffect(() => {
    if (hasLoadedRef.current) {
      load();
    }
  }, [filters, page, perPage]);

  const handleSearch = () => {
    setPage(1);
    setFilters({ search: searchTerm.trim() || undefined });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this awardee? Only allowed when no tenders are linked.')) return;
    try {
      await tenderAwardeeService.delete(id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to delete');
    }
  };

  const viewTenders = (awardeeId: number) => {
    navigate('/tenders', { state: { restore: { filters: { awardee_id: awardeeId }, page: 1, perPage: 50 } } });
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Tender Awardees</h1>
            <p className="text-sm text-gray-600">Master list of companies and persons linked to tenders</p>
          </div>
          <button
            type="button"
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            <Icon component={FiPlus} className="w-4 h-4" />
            Add Awardee
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <div className="bg-white p-3 rounded-lg shadow-sm flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Name, phone, email…"
                className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
              <button type="button" onClick={handleSearch} className="px-3 py-1.5 bg-gray-100 rounded-md hover:bg-gray-200">
                <Icon component={FiSearch} className="w-4 h-4" />
              </button>
              {(searchTerm || filters.search) && (
                <button
                  type="button"
                  onClick={() => { setSearchTerm(''); setFilters({}); setPage(1); }}
                  className="px-2 py-1.5 text-gray-600 hover:text-gray-900"
                  aria-label="Clear search"
                >
                  <Icon component={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">City</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Phone</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Created</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && awardees.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Loading…</td></tr>
              ) : awardees.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No awardees found</td></tr>
              ) : (
                awardees.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                        <Link to={`/tender-awardees/${a.id}`} className="text-yellow-700 hover:underline truncate">
                          {a.name}
                        </Link>
                        <AwardeeTenderCountPill
                          count={a.tender_count ?? 0}
                          awardeeId={a.id}
                          onFilterTenders={viewTenders}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{a.awardee_city || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{a.awardee_phone || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{a.awardee_email || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{a.created_at ? formatDate(a.created_at) : '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Link
                        to={`/tender-awardees/${a.id}`}
                        className="text-xs text-yellow-700 hover:underline mr-2"
                      >
                        Profile
                      </Link>
                      <button
                        type="button"
                        onClick={() => viewTenders(a.id)}
                        className="text-xs text-gray-600 hover:underline mr-2"
                      >
                        Filter list
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditing(a); setShowModal(true); }}
                        className="p-1 text-gray-600 hover:text-yellow-700"
                        aria-label="Edit"
                      >
                        <Icon component={FiEdit2} className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(a.id)}
                        className="p-1 text-gray-600 hover:text-red-600 ml-1"
                        aria-label="Delete"
                      >
                        <Icon component={FiTrash2} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          <ListPagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
          />
        </div>
      </div>

      <AwardeeFormModal
        open={showModal}
        editing={editing}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSaved={() => { setShowModal(false); setEditing(null); load(); }}
      />
    </Layout>
  );
};

export default TenderAwardees;
